import { useEffect, useState } from 'react';
import {
  KeyRound as LockIcon,
  LogOut,
  RefreshCw,
  RotateCcw,
  Search,
  Users,
  FolderX,
  FileArchive,
  ChevronLeft,
  MoreVertical,
  HardDriveDownload,
  FileWarning,
} from 'lucide-react';
import { useVault } from '../../../core/lib/vault';
import { pullAll, flushOutbox, subscribe as subscribeSync } from '../sync';
import { connectSync, disconnectSync, subscribeChanges } from '../sync/ws';
import { useBreakpoint } from '../../../core/lib/useBreakpoint';
import { localStore } from '../db';
import { StatusLight } from '../../../core/components/StatusLight';
import { PortalHeader } from '../../../core/components/PortalHeader';
import { resolveAppIcon } from '../../../core/lib/appIcons';
import { useOnline, useConnectionMode, useHeartbeatTick } from '../lib/useOnline';
import { useCanAdminister } from '../lib/usePermissions';
import { useSyncStatus, totalPending } from '../lib/useSyncStatus';
import {
  onPermanentSnapshotError,
  onConflictMerged,
  subscribeLocalCacheFull,
} from '../lib/yjsCollab';
import { onPermanentOutboxError } from '../sync';
import { CustomerPane } from './CustomerPane';
import { DocumentPane } from './DocumentPane';
import { EditorPane } from './EditorPane';
import { AttachmentsView } from './AttachmentsView';
import { GlobalSearch } from './GlobalSearch';
import { MembersModal } from './MembersModal';
import { OrphanageModal } from './OrphanageModal';
import { ExportVaultModal } from './ExportVaultModal';
import { BackupSettingsModal } from './BackupSettingsModal';
import { AttachmentPreviewModal } from './AttachmentPreviewModal';
import { ResyncFromServerModal } from './ResyncFromServerModal';
import { FailedEditsModal } from './FailedEditsModal';
import { subscribeAttachmentPreview } from '../lib/attachmentPreview';
import {
  onYjsNak,
  flushDoc,
  flushAllLiveDocs,
  flushAllLiveDocsForUnload,
  writeLocalMirror,
  getLoadedDocIds,
  recoverPendingDocuments,
} from '../lib/yjsCollab';

/**
 * Fire a flushDoc that must NEVER block the caller. Navigation clicks used
 * to `await flushDoc(...)`, but if the PUT hung (slow network, 2-in-a-row
 * 409, or anything else that throws), the click handler's post-await state
 * changes never ran and the user's next click appeared dead. Layer 2's
 * `releaseDocAsync` already keeps the Y.Doc alive across the flush window,
 * and Layer 6's 500 ms localStore mirror already made the last burst
 * durable — so the flush from the click path is "prompt best-effort", not
 * load-bearing.
 */
function kickFlush(docId: string): void {
  void flushDoc(docId).catch((err) => {
    console.warn('[vault] flushDoc failed on navigation — outbox will retry', err);
  });
}

// Display label + icon name shown in the PortalHeader app-pill.
const APP_NAME = 'Documentation';
const APP_ICON = 'file-text';

// Resume-where-you-left-off persistence. Selection state is mirrored to the
// IndexedDB `meta` store on every change and read back on Workspace mount —
// so re-entering the Master Code after auto-lock OR a page refresh lands
// the user on the same customer/page/tab. Sign-out runs `localStore.wipe()`
// (via the SignOutPrep callback in `apps/vault/index.tsx`) which clears the
// `meta` store along with everything else, so a saved position never leaks
// to the next user. Stale entries past the TTL are dropped on read.
const LAST_POSITION_KEY = 'vault.lastPosition';
const RESUME_TTL_MS = 60 * 60 * 1000;

interface StoredLastPosition {
  selectedCustomer: string | null;
  selectedPage: string | null;
  selectedTab: string | null;
  attachmentsMode: boolean;
  savedAt: number;
}

export function Workspace() {
  const lock = useVault((s) => s.lock);
  const signOut = useVault((s) => s.signOut);
  const online = useOnline();
  const connectionMode = useConnectionMode();
  const heartbeatTick = useHeartbeatTick();
  const offlineLabel =
    connectionMode === 'locked'
      ? 'Locked — sync paused'
      : connectionMode === 'expired'
        ? 'Re-auth needed — sync paused'
        : 'Offline — editing paused';
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  /** Null = "Main" tab (the page itself); otherwise the sub-doc id. */
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showOrphanage, setShowOrphanage] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showBackup, setShowBackup] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const [showResyncFromServer, setShowResyncFromServer] = useState(false);
  const [showFailedEdits, setShowFailedEdits] = useState(false);
  /**
   * Latches true once the post-mount boot `pullAll + flushOutbox` resolves.
   * EditorPane gates `acquireDoc` on this so it never seeds a Y.Doc from a
   * pre-pull (potentially stale-from-before-lock) IDB row. Without this,
   * unlocking after a long idle could surface yesterday's Y.Doc body backed
   * by today's `lastKnownVersion` until a manual Resync. See plan F1.
   */
  const [initialSyncComplete, setInitialSyncComplete] = useState(false);
  const [previewAttachmentId, setPreviewAttachmentId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [deleteNotice, setDeleteNotice] = useState<string | null>(null);
  const [attachmentsMode, setAttachmentsMode] = useState(false);
  const [pendingOps, setPendingOps] = useState(0);
  const [permanentError, setPermanentError] = useState<string | null>(null);
  const [conflictNotice, setConflictNotice] = useState<string | null>(null);
  // Flips to true once the restore-on-mount effect has finished reading IDB
  // (whether or not it found anything to restore). The save effect bails
  // until then so the initial null/null/null/false state can't race-write
  // over a still-being-read stored position.
  const [resumeRestored, setResumeRestored] = useState(false);
  const syncStatus = useSyncStatus();

  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'mobile';
  const isDesktop = breakpoint === 'desktop';
  // Admin-only (owner | admin) gate for Export / Members menu items.
  // Orphanage stays visible to everyone — all members can VIEW deleted
  // items; only admins see restore buttons inside the modal.
  const canAdminister = useCanAdminister();

  // Derive which pane is active on mobile. The Attachments view lives in the
  // same right-hand slot as the editor, so it piggybacks on `'editor'`.
  const activePane: 'customers' | 'documents' | 'editor' =
    attachmentsMode && selectedCustomer
      ? 'editor'
      : selectedPage
        ? 'editor'
        : selectedCustomer
          ? 'documents'
          : 'customers';

  function goBack() {
    if (activePane === 'editor') {
      if (attachmentsMode) {
        setAttachmentsMode(false);
      } else {
        const currentDocId = selectedTab ?? selectedPage;
        if (currentDocId) kickFlush(currentDocId);
        setSelectedPage(null);
        setSelectedTab(null);
      }
    } else if (activePane === 'documents') {
      setSelectedCustomer(null);
    }
  }

  useEffect(() => subscribeAttachmentPreview((id) => setPreviewAttachmentId(id)), []);

  // After Dropbox OAuth round-trip, the server 302s back to /?dropbox=connected.
  // Auto-open the Backups modal so the admin sees the new connected state, then
  // strip the query string so a refresh doesn't re-trigger.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('dropbox') === 'connected') {
      setShowBackup(true);
      params.delete('dropbox');
      const qs = params.toString();
      const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Restore the last customer/page/tab/attachments-mode if the saved entry
  // is fresh. Functional setters preserve any selection the user made
  // during the brief async IDB read window — if the user clicked a customer
  // before getMeta() resolved, we don't clobber their click.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await localStore.getMeta(LAST_POSITION_KEY);
        if (cancelled || !raw) return;
        const parsed = JSON.parse(raw) as Partial<StoredLastPosition>;
        const fresh =
          typeof parsed.savedAt === 'number' &&
          Date.now() - parsed.savedAt <= RESUME_TTL_MS;
        if (!fresh) {
          // Soft-clear so a long-stale row doesn't sit forever.
          await localStore.setMeta(LAST_POSITION_KEY, '');
          return;
        }
        if (typeof parsed.selectedCustomer === 'string') {
          const v = parsed.selectedCustomer;
          setSelectedCustomer((prev) => prev ?? v);
        }
        if (typeof parsed.selectedPage === 'string') {
          const v = parsed.selectedPage;
          setSelectedPage((prev) => prev ?? v);
        }
        if (typeof parsed.selectedTab === 'string') {
          const v = parsed.selectedTab;
          setSelectedTab((prev) => prev ?? v);
        }
        if (parsed.attachmentsMode === true) {
          setAttachmentsMode((prev) => prev || true);
        }
      } catch {
        /* missing entry, bad JSON, IDB error — bail silently */
      } finally {
        if (!cancelled) setResumeRestored(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Mirror selection to IDB on every change so a refresh / auto-lock can
  // restore it. Gated on `resumeRestored` so the initial empty state can't
  // overwrite a stored position before the restore effect has read it.
  useEffect(() => {
    if (!resumeRestored) return;
    const payload: StoredLastPosition = {
      selectedCustomer,
      selectedPage,
      selectedTab,
      attachmentsMode,
      savedAt: Date.now(),
    };
    void localStore
      .setMeta(LAST_POSITION_KEY, JSON.stringify(payload))
      .catch(() => undefined);
  }, [resumeRestored, selectedCustomer, selectedPage, selectedTab, attachmentsMode]);

  // Keep the outbox-pending count reactive: re-query after every sync
  // notification (enqueue, flush, pullAll). Cheap — IndexedDB listOps().
  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      try {
        const ops = await localStore.listOps();
        if (!cancelled) setPendingOps(ops.length);
      } catch {
        /* ignore */
      }
    }
    void refresh();
    return subscribeSync(() => void refresh());
  }, []);

  // Permanent-failure surfacing. A snapshot or outbox op that gets a
  // 404/401/403/410 cannot be retried — the doc is gone, the user lost
  // auth, or their role was revoked. Show a banner the user can dismiss
  // so silent loss is impossible.
  useEffect(() => {
    const offSnap = onPermanentSnapshotError((e) => {
      const msg =
        e.status === 404
          ? 'A page you were editing was deleted on the server. Your edits could not be saved. Use Resync from server to clear your local cache.'
          : e.status === 401
            ? 'Your session has expired. Sign in again to continue saving edits.'
            : e.status === 403
              ? 'Your access was revoked. Edits cannot be saved.'
              : `An edit could not be saved: ${e.message}`;
      setPermanentError(msg);
    });
    const offOut = onPermanentOutboxError((e) => {
      setPermanentError(
        `A queued change failed permanently (${e.status}): ${e.message}. It has been dropped from the queue.`,
      );
    });
    // Conflict-merge toast: one auto-dismissing notice per merge so the user
    // can see a peer landed edits at the same time, instead of being puzzled
    // by an unexplained text shift. CRDT means no edits were lost on either
    // side; this is purely awareness.
    let conflictTimer: ReturnType<typeof setTimeout> | null = null;
    const offConflict = onConflictMerged(() => {
      setConflictNotice('Merged with another editor’s changes.');
      if (conflictTimer) clearTimeout(conflictTimer);
      conflictTimer = setTimeout(() => setConflictNotice(null), 4000);
    });
    return () => {
      offSnap();
      offOut();
      offConflict();
      if (conflictTimer) clearTimeout(conflictTimer);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setSyncing(true);
    // Boot sequence:
    //  1. pullAll  — fetch the server's current state into IndexedDB.
    //  2. flushOutbox — push any queued mutations from a prior session.
    //  3. recoverPendingDocuments — for any document row still flagged
    //     pendingSync (e.g., a snapshot PUT that never landed last session,
    //     or a sign-out without a successful flush), retry the snapshot via
    //     yjsCollab's infinite-retry loop. Either it lands, or a permanent
    //     error fires for the user to resolve.
    Promise.all([pullAll(), flushOutbox()])
      .then(() => {
        if (!mounted) return;
        // Latch the gate BEFORE recoverPendingDocuments — the latter is a
        // long-running background loop and the editor only needs IDB to be
        // up-to-date with the server, which pullAll has just guaranteed.
        setInitialSyncComplete(true);
        const vk = useVault.getState().vaultKey;
        if (vk) return recoverPendingDocuments(vk);
        return undefined;
      })
      .catch(() => {
        // Even on pullAll failure, lift the gate so the user isn't stuck
        // on a skeleton forever — the editor will fall back to whatever IDB
        // has, which is the same behaviour as before this fix.
        if (mounted) setInitialSyncComplete(true);
      })
      .finally(() => mounted && setSyncing(false));
    connectSync();
    return () => {
      mounted = false;
      disconnectSync();
    };
  }, []);

  // Recurring outbox flush. Any queued op a transient failure halted on
  // will re-attempt every 30 s while the user is on the app. Cheap when
  // the queue is empty (a single IndexedDB listOps()).
  useEffect(() => {
    const id = setInterval(() => {
      void flushOutbox().catch((err) => console.warn('[vault] periodic flushOutbox', err));
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  // Browser `online` event — fires when the OS regains network even if
  // our WS reconnect timer hasn't fired yet. Drains the outbox and pushes
  // any abandoned-pending docs immediately rather than waiting on the
  // 30 s timer or the WS backoff.
  useEffect(() => {
    function onOnline() {
      void flushOutbox().catch((err) => console.warn('[vault] online flushOutbox', err));
      const vk = useVault.getState().vaultKey;
      if (vk) {
        void recoverPendingDocuments(vk).catch((err) =>
          console.warn('[vault] online recoverPendingDocuments', err),
        );
      }
    }
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  // Tab-close / navigate-away flush. `pagehide` fires on mobile Safari too
  // (where `beforeunload` doesn't). Three layers of defense, since the
  // browser doesn't block on async work in this handler:
  //   1. `flushAllLiveDocsForUnload()` dispatches keepalive PUTs that the
  //      browser is required to let finish past unload (64 KB body cap).
  //   2. `flushAllLiveDocs()` kicks the normal retry-loop path for any doc
  //      whose snapshot exceeds the keepalive cap (browsers usually keep
  //      these alive briefly anyway, and worst-case it lands on next boot).
  //   3. Synchronous IDB mirror as last line — `acquireDoc` on reopen seeds
  //      from at most LOCAL_MIRROR_IDLE_MS of staleness.
  useEffect(() => {
    function onHide() {
      flushAllLiveDocsForUnload();
      void flushAllLiveDocs();
      for (const docId of getLoadedDocIds()) {
        void writeLocalMirror(docId);
      }
    }
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // Auto-navigate away from a document/customer that was deleted by someone
  // else (or by us in another tab). We read the latest selection via refs so
  // the subscription doesn't need to reattach on every change.
  useEffect(() => {
    return subscribeChanges((ev) => {
      if (ev.action !== 'delete') return;
      if (ev.resource === 'documents') {
        if (ev.id === selectedTab) {
          setSelectedTab(null);
          setDeleteNotice('This tab was deleted. Moved back to the main page.');
        } else if (ev.id === selectedPage) {
          setSelectedPage(null);
          setSelectedTab(null);
          setDeleteNotice('This page was deleted. Moved back to the page list.');
        }
      } else if (ev.resource === 'customers' && ev.id === selectedCustomer) {
        setSelectedCustomer(null);
        setSelectedPage(null);
        setSelectedTab(null);
        setDeleteNotice('This customer was deleted. Moved back to the customer list.');
      }
    });
  }, [selectedCustomer, selectedPage, selectedTab]);

  // Auto-dismiss the delete notice after 4s.
  useEffect(() => {
    if (!deleteNotice) return;
    const t = setTimeout(() => setDeleteNotice(null), 4000);
    return () => clearTimeout(t);
  }, [deleteNotice]);

  // Surface a one-shot warning when the IndexedDB crash-recovery mirror
  // hits the browser's storage quota. Server PUTs continue to work; only
  // the second-line crash-recovery defense is degraded. Without this the
  // user has no idea their tab-close protection is broken.
  useEffect(() => {
    return subscribeLocalCacheFull(() => {
      setDeleteNotice(
        'Local crash-recovery cache is full — sync continues, but tab-close protection is reduced. Clear browser storage or sign out other tabs to restore it.',
      );
    });
  }, []);

  // Surface a NAK toast when the server rejects an outbound Yjs update.
  // Oversized frames used to vanish silently — now the sender sees why.
  useEffect(() => {
    return onYjsNak((ev) => {
      if (ev.reason === 'too_large') {
        const kb = ev.maxBytes ? Math.round(ev.maxBytes / 1024) : 256;
        setDeleteNotice(
          `That edit was too large to broadcast (over ${kb} KB). Split it into smaller pastes so it syncs to other devices.`,
        );
      } else if (ev.reason === 'not_member') {
        setDeleteNotice('You no longer have access to this vault. Reload to continue.');
      }
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      } else if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'L') {
        e.preventDefault();
        lock();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lock]);

  // Close overflow menu on outside click
  useEffect(() => {
    if (!showOverflow) return;
    const close = () => setShowOverflow(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [showOverflow]);

  async function resync() {
    setSyncing(true);
    try {
      await pullAll();
      await flushOutbox();
    } finally {
      setSyncing(false);
    }
  }

  /**
   * Open the "Resync from server" modal. The modal owns the destructive
   * `localStore.wipe()` + `pullAll()` sequence and gates it behind a
   * pending-state probe + typed confirmation when anything is unsynced —
   * the bare `window.confirm` it replaced warned about lost edits but
   * never actually inspected what was pending, so a user who believed
   * (correctly per the green status light) that everything was synced
   * could destroy IDB-only writes the outbox had silently dropped as
   * permanent errors. See plan F2.
   */
  function resyncFromServer() {
    setShowResyncFromServer(true);
  }

  // Pane visibility helpers
  // Tablet shows all three panes (like desktop) — compact customer + narrow docs + editor.
  // Mobile drills down one pane at a time.
  const showCustomerPane =
    isDesktop || breakpoint === 'tablet' || (isMobile && activePane === 'customers');
  const showDocumentPane =
    isDesktop || breakpoint === 'tablet' || (isMobile && activePane === 'documents');
  const showEditorPane =
    isDesktop || breakpoint === 'tablet' || (isMobile && activePane === 'editor');

  return (
    <div className="h-dvh flex flex-col bg-ink-950">
      <PortalHeader
        leadingAction={
          isMobile && activePane !== 'customers' ? (
            <>
              <button onClick={goBack} className="btn-ghost !p-1 -ml-1">
                <ChevronLeft size={20} />
              </button>
              <span className="font-semibold truncate">
                {activePane === 'documents'
                  ? customerName || 'Customer'
                  : 'Back'}
              </span>
            </>
          ) : undefined
        }
        appContext={
          isMobile && activePane !== 'customers'
            ? undefined
            : {
                icon: resolveAppIcon(APP_ICON),
                name: APP_NAME,
                // Single-app build — the logo renders as plain branding.
              }
        }
        center={
          isDesktop ? (
            <button
              onClick={() => setShowSearch(true)}
              className="flex items-center gap-2 flex-1 max-w-xl rounded-md border border-ink-700 bg-ink-800 px-3 py-1.5 text-sm text-ink-400 hover:border-ink-600"
            >
              <Search size={14} />
              <span>Search customers, pages, content…</span>
              <span className="ml-auto text-xs text-ink-500">
                <kbd>Ctrl</kbd>+<kbd>K</kbd>
              </span>
            </button>
          ) : undefined
        }
        right={
          <div className="relative flex items-center gap-2">
            <StatusLight
              online={online}
              syncing={syncing}
              pendingOps={Math.max(pendingOps, totalPending(syncStatus))}
              retrying={syncStatus.retryingDocCount > 0}
              permanentlyFailed={syncStatus.permanentlyFailedCount}
              onPermanentClick={() => setShowFailedEdits(true)}
              heartbeatTick={heartbeatTick}
              lastError={syncStatus.lastSnapshotError}
              offlineLabel={offlineLabel}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowOverflow((v) => !v);
              }}
              className="btn-ghost"
              title="More"
            >
              <MoreVertical size={14} />
            </button>
            {showOverflow && (
              <div className="absolute right-0 top-full mt-1 w-48 rounded-md border border-ink-700 bg-ink-900 shadow-lg py-1 z-50">
                <button
                  onClick={resync}
                  disabled={syncing}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                >
                  <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} /> Resync
                </button>
                <button
                  onClick={resyncFromServer}
                  disabled={syncing}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                  title="Wipe local cache and reload everything from the server"
                >
                  <RotateCcw size={14} /> Resync from server
                </button>
                {syncStatus.permanentlyFailedCount > 0 && (
                  <button
                    onClick={() => {
                      setShowFailedEdits(true);
                      setShowOverflow(false);
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-300 hover:bg-ink-800"
                  >
                    <FileWarning size={14} /> Recover failed edits (
                    {syncStatus.permanentlyFailedCount})
                  </button>
                )}
                <button
                  onClick={() => setShowOrphanage(true)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                >
                  <FolderX size={14} /> Orphanage
                </button>
                {canAdminister && (
                  <>
                    <button
                      onClick={() => setShowExport(true)}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                    >
                      <FileArchive size={14} /> Export
                    </button>
                    <button
                      onClick={() => setShowMembers(true)}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                    >
                      <Users size={14} /> Members
                    </button>
                    <button
                      onClick={() => setShowBackup(true)}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                    >
                      <HardDriveDownload size={14} /> Backups
                    </button>
                  </>
                )}
                <div className="border-t border-ink-800 mt-1 pt-1">
                  <button
                    onClick={lock}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                  >
                    <LockIcon size={14} /> Lock vault
                  </button>
                  <button
                    onClick={signOut}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        }
        overflow={
          <div className="relative flex items-center gap-2">
            <button onClick={() => setShowSearch(true)} className="btn-ghost" title="Search">
              <Search size={16} />
            </button>
            <StatusLight
              online={online}
              syncing={syncing}
              pendingOps={Math.max(pendingOps, totalPending(syncStatus))}
              retrying={syncStatus.retryingDocCount > 0}
              permanentlyFailed={syncStatus.permanentlyFailedCount}
              onPermanentClick={() => setShowFailedEdits(true)}
              heartbeatTick={heartbeatTick}
              lastError={syncStatus.lastSnapshotError}
              offlineLabel={offlineLabel}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowOverflow((v) => !v);
              }}
              className="btn-ghost"
              title="More"
            >
              <MoreVertical size={16} />
            </button>
            {showOverflow && (
              <div className="absolute right-0 top-full mt-1 w-48 rounded-md border border-ink-700 bg-ink-900 shadow-lg py-1 z-50">
                <button
                  onClick={resync}
                  disabled={syncing}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                >
                  <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} /> Resync
                </button>
                <button
                  onClick={resyncFromServer}
                  disabled={syncing}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                  title="Wipe local cache and reload everything from the server"
                >
                  <RotateCcw size={14} /> Resync from server
                </button>
                {syncStatus.permanentlyFailedCount > 0 && (
                  <button
                    onClick={() => {
                      setShowFailedEdits(true);
                      setShowOverflow(false);
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-300 hover:bg-ink-800"
                  >
                    <FileWarning size={14} /> Recover failed edits (
                    {syncStatus.permanentlyFailedCount})
                  </button>
                )}
                <button
                  onClick={() => setShowOrphanage(true)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                >
                  <FolderX size={14} /> Orphanage
                </button>
                {canAdminister && (
                  <>
                    <button
                      onClick={() => setShowExport(true)}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                    >
                      <FileArchive size={14} /> Export
                    </button>
                    <button
                      onClick={() => setShowMembers(true)}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                    >
                      <Users size={14} /> Members
                    </button>
                    <button
                      onClick={() => setShowBackup(true)}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                    >
                      <HardDriveDownload size={14} /> Backups
                    </button>
                  </>
                )}
                <div className="border-t border-ink-800 mt-1 pt-1">
                  <button
                    onClick={lock}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                  >
                    <LockIcon size={14} /> Lock vault
                  </button>
                  <button
                    onClick={signOut}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-ink-200 hover:bg-ink-800"
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        }
      />

      {/* Indeterminate progress bar shown while pullAll/flushOutbox is running.
          Sits between header and the three-pane area so it's always visible. */}
      <div className="relative h-0.5 bg-ink-800/60 overflow-hidden" aria-hidden>
        {syncing && (
          <div className="absolute inset-y-0 w-1/3 bg-dragon-500 animate-sweep" />
        )}
      </div>

      <div
        className={`flex-1 min-h-0 flex transition-opacity duration-200 ${
          syncing ? 'opacity-80' : 'opacity-100'
        }`}
      >
        {showCustomerPane && (
          <CustomerPane
            compact={breakpoint === 'tablet'}
            selectedId={selectedCustomer}
            onSelect={(id, name) => {
              // Kick a best-effort flush. We do NOT await — a hanging PUT
              // must never block the click. releaseDocAsync keeps the
              // Y.Doc alive across the flush; the localStore mirror is
              // already fresh (500 ms debounce).
              const currentDocId = selectedTab ?? selectedPage;
              if (currentDocId) kickFlush(currentDocId);
              setSelectedCustomer(id);
              setCustomerName(name ?? '');
              setSelectedPage(null);
              setSelectedTab(null);
              setAttachmentsMode(false);
            }}
          />
        )}
        {showDocumentPane && (
          <DocumentPane
            customerId={selectedCustomer}
            selectedDocId={selectedPage}
            onSelect={(id) => {
              const currentDocId = selectedTab ?? selectedPage;
              if (currentDocId && currentDocId !== id) {
                kickFlush(currentDocId);
              }
              setSelectedPage(id);
              setSelectedTab(null);
              setAttachmentsMode(false);
            }}
            onOpenAttachments={() => {
              const currentDocId = selectedTab ?? selectedPage;
              if (currentDocId) kickFlush(currentDocId);
              setAttachmentsMode(true);
            }}
          />
        )}
        {showEditorPane &&
          (attachmentsMode && selectedCustomer ? (
            <AttachmentsView
              customerId={selectedCustomer}
              customerName={customerName}
              onClose={() => setAttachmentsMode(false)}
              onNavigateToDoc={(pageId, tabId) => {
                const currentDocId = selectedTab ?? selectedPage;
                const nextDocId = tabId ?? pageId;
                if (currentDocId && currentDocId !== nextDocId) {
                  kickFlush(currentDocId);
                }
                setAttachmentsMode(false);
                setSelectedPage(pageId);
                setSelectedTab(tabId);
              }}
            />
          ) : (
            <EditorPane
              pageId={selectedPage}
              activeTabId={selectedTab}
              onTabSelect={setSelectedTab}
              initialSyncComplete={initialSyncComplete}
            />
          ))}
      </div>

      {showSearch && (
        <GlobalSearch
          onClose={() => setShowSearch(false)}
          onSelect={(customerId, pageId, tabId) => {
            const currentDocId = selectedTab ?? selectedPage;
            const nextDocId = tabId ?? pageId;
            if (currentDocId && currentDocId !== nextDocId) {
              kickFlush(currentDocId);
            }
            setSelectedCustomer(customerId);
            setSelectedPage(pageId);
            setSelectedTab(tabId);
            setShowSearch(false);
          }}
        />
      )}
      {showResyncFromServer && (
        <ResyncFromServerModal
          onClose={() => setShowResyncFromServer(false)}
          onComplete={() => setShowResyncFromServer(false)}
        />
      )}
      {showFailedEdits && (
        <FailedEditsModal onClose={() => setShowFailedEdits(false)} />
      )}
      {showMembers && <MembersModal onClose={() => setShowMembers(false)} />}
      {showOrphanage && <OrphanageModal onClose={() => setShowOrphanage(false)} />}
      {showExport && <ExportVaultModal onClose={() => setShowExport(false)} />}
      {showBackup && <BackupSettingsModal onClose={() => setShowBackup(false)} />}
      {previewAttachmentId && (
        <AttachmentPreviewModal
          attachmentId={previewAttachmentId}
          onClose={() => setPreviewAttachmentId(null)}
        />
      )}
      {deleteNotice && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 rounded-md bg-amber-950/90 border border-amber-900 px-4 py-2 text-xs text-amber-200 shadow-lg">
          {deleteNotice}
        </div>
      )}
      {conflictNotice && (
        <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-50 rounded-md bg-ink-900/95 border border-ink-700 px-4 py-2 text-xs text-ink-200 shadow-lg">
          {conflictNotice}
        </div>
      )}
      {permanentError && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-md rounded-md bg-red-950/90 border border-red-900 px-4 py-2 text-xs text-red-200 shadow-lg flex items-start gap-3">
          <span className="flex-1">{permanentError}</span>
          <button
            onClick={() => setPermanentError(null)}
            className="text-red-300 hover:text-red-100"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
